import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import PropTypes from 'prop-types';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import { MDXProvider } from '@mdx-js/react';
import { MDXRenderer } from 'gatsby-plugin-mdx';

import Container from 'components/Container';
import { Topbar, Sidebar, Footer, FilterDiagram } from './components';
import pages from '../navigation--docs';
import { makeStyles } from '@material-ui/core/styles';

import Slugger from 'github-slugger';
import { graphql, Link } from 'gatsby';
import { useLocation } from '@reach/router';
import Tooltip from '@mui/material/Tooltip';
import Highlight, { defaultProps } from 'prism-react-renderer'
import Typography from '@material-ui/core/Typography';

import ExternalLinkIcon from '@material-ui/icons/OpenInNewSharp';
import TipIcon from '@material-ui/icons/EmojiObjectsSharp';

import HighlightTheme from 'prism-react-renderer/themes/vsDark';
import copy from 'copy-to-clipboard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';

const FONT_TITLE = '"Titillium Web", sans-serif';
const FONT_TOC = '"Titillium Web", verdana, sans-serif';
const FONT_TEXT = '"Titillium Web", graphik, verdana, arial, sans-serif';
const FONT_CODE = 'menlo, monaco, "Courier New", monospace';

const useStyles = makeStyles(theme => ({
  head: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: theme.TOOLBAR_HEIGHT,
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'stretch',
    backgroundColor: '#222',
  },

  home: {
    width: theme.TAB_WIDTH,
    height: theme.TOOLBAR_HEIGHT,
    paddingLeft: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },

  tools: {
    height: theme.TOOLBAR_HEIGHT,
    flexGrow: 1,
    paddingRight: theme.spacing(5),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'right',
  },

  logo: {
    width: '36px',
    cursor: 'pointer',
    transition: 'opacity 0.5s',
    '&:hover': {
      opacity: '60%',
    },
  },

  nav: {
    position: 'absolute',
    top: theme.TOOLBAR_HEIGHT,
    bottom: 0,
    width: '300px',
    padding: theme.spacing(2),
    overflowY: 'scroll',
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      width: '8px',
      backgroundColor: '#303030',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#383838',
    },
  },

  navTitle: {
    fontFamily: FONT_TITLE,
    fontSize: '1rem',
    paddingLeft: theme.spacing(2),
  },

  navItem: {
    height: '26px',
  },

  navItemText: {
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    '&:hover': {
      textDecorationLine: 'underline',
    },
  },

  navItemTextParent: {
    color: theme.palette.text.primary,
    cursor: 'pointer',
    '&:hover': {
      textDecorationLine: 'underline',
    },
    fontWeight: 'bold',
  },

  navItemTextCurrent: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecorationLine: 'underline',
    },
    fontWeight: 'bold',
  },

  navGroup: {
    paddingLeft: theme.spacing(2),
  },

  toc: {
    position: 'fixed',
    right: '0',
    top: '15%',
    width: '260px',
    maxHeight: 510,
    bottom: 0,
    right: 0,
    padding: theme.spacing(2),
    borderLeft: '3px solid #ececec',
    overflow: 'auto',
  },

  tocTitle: {
    fontFamily: FONT_TITLE,
    fontSize: '11pt',
    color: '#00adef',
  },

  tocList: {
    listStyleType: 'none',
    paddingInlineStart: 0,
    marginInlineStart: 0,
    fontFamily: FONT_TOC,
    fontSize: '0.9rem',
  },

  tocListItem: {
    fontFamily: FONT_TOC,
    color: '#00adef',
		display: 'block',
		padding: '4px 0',
    textDecorationLine: 'none',
    '&:hover': {
	    fontWeight: 700,
    },
  },

  main: {
    position: 'absolute',
    top: theme.TOOLBAR_HEIGHT,
    bottom: 0,
    left: '300px',
    right: '300px',
    padding: theme.spacing(2),
    overflowY: 'scroll',
    '&::-webkit-scrollbar': {
      width: '8px',
      backgroundColor: '#303030',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#383838',
    },
  },

  title: {
    fontFamily: FONT_TITLE,
    fontSize: '3rem',
		marginBottom: '60px',
  },

  img: { maxWidth: '100%'},

  h1: { pointerEvents:'none', fontFamily: FONT_TITLE, fontSize: '2.0rem' , marginTop:'-90px',paddingTop:'90px', position:'relative'},
  h2: { pointerEvents:'none', fontFamily: FONT_TITLE, fontSize: '1.5rem' , marginTop:'-90px',paddingTop:'90px',position:'relative'},
  h3: { pointerEvents:'none', fontFamily: FONT_TITLE, fontSize: '1.3rem' , marginTop:'-90px',paddingTop:'90px',position:'relative'},
  h4: { pointerEvents:'none', fontFamily: FONT_TITLE, fontSize: '1.1rem' , marginTop:'-90px',paddingTop:'90px',position:'relative'},

  p: {
    fontFamily: FONT_TEXT,
    fontSize: '1.0rem',
    lineHeight: '1.8rem',
  },

  table: {
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  tip: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    margin: 0,
    backgroundColor: 'rgba(221,238,255,0.4)',
    borderLeftStyle: 'solid',
    borderLeftWidth: '3px',
    borderLeftColor: '#fff48f',
  },

  tipIcon: {
    flexGrow: 0,
    color: '#00adef',
    position:'relative',
    top: -3,
    paddingTop: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },

  tipText: {
    flexGrow: 1,
    fontFamily: FONT_TEXT,
    fontSize: '1.0rem',
    lineHeight: '1.8rem',
  },

  link: {
    color: '#3399ff',
    textDecorationLine: 'underline',
    textUnderlinePosition: 'under',
    '&:hover': {
      textDecorationLine: 'none',
    },
  },

  externalLinkIcon: {
    verticalAlign: 'sub',
  },

  codeBox: {
    fontFamily: FONT_CODE,
    fontSize: '0.8rem',
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: 0,
	  borderRadius: 10,
		overflow: 'auto',
    borderLeftStyle: 'solid',
    borderLeftWidth: '3px',
    borderLeftColor: '#fff48f',
  },

  codeSame: {
    opacity: '70%',
  },

  codeAdded: {
    backgroundColor: '#1e3e1e',
  },

  codeDeleted: {
    backgroundColor: '#3e1e1e',
    textDecorationLine: 'line-through',
  },

  inlineCode: {
    fontFamily: FONT_CODE,
    fontSize: '1.0rem',
    paddingLeft: theme.spacing(0.6),
    paddingRight: theme.spacing(0.6),
    paddingTop: '0px',
    paddingBottom: '1px',
	  borderRadius: 4,
    backgroundColor: '#fff48f',
    color: '#181818',
  },

  memberName: {
    fontFamily: FONT_CODE,
    fontSize: '1.0rem',
    fontWeight: 'bold',
    color: '#00adef',
    padding: theme.spacing(0.8),
    backgroundColor: theme.palette.text.codeBox,
    textDecorationLine: 'underline',
    textUnderlinePosition: 'under',
    '&:hover': {
      textDecorationLine: 'none',
    },
  },

  parameterName: {
    fontFamily: FONT_CODE,
    fontSize: '1.0rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    padding: theme.spacing(0.8),
    backgroundColor: theme.palette.text.codeBox,
  },

  description: {
    paddingInlineStart: theme.spacing(3),
    fontFamily: FONT_TEXT,
    fontSize: '1.0rem',
  },

  selected: {
    color: theme.palette.primary.main,
  },
	
	copyIcon: {
		position:"absolute",
		right:'10px',
		top:'10px',
		color:'#fff',
		zIndex:10,
		cursor:'pointer'
	}
}));

export const query = graphql`
  query(
    $id: String
    $api: String
  ) {
    mdx(id: {eq: $id}) {
      frontmatter {
        title
      }
      body
      headings {
        depth
        value
      }
    }
    typeDoc(filename: {eq: $api}) {
      internal {
        content
      }
    }
    allMdx {
      nodes {
        fields {
          path
          lang
        }
				rawBody
				slug
        frontmatter {
          title
          api
        }
      }
    }
  }
`;

const makeStyledTag = (tag, style) => {
  style = style || tag;
  return ({ children, ...props }) => {
    const classes = useStyles();
    const Tag = tag;
    const className = classes[style];
    return (
      <Tag {...props} className={className}>
        {children}
      </Tag>
    );
  };
}

const SourceCode = ({ children, className }) => {
  const classes = useStyles();
  const language = className === 'language-js' ? 'javascript' : undefined;
  const lines = children.split('\n');
  let isDiff = true;
  const [isCopied, setCopied] = React.useState(false);
  const colors = lines.map(
    line => {
      if (line.trim() === '') return;
      if (line.startsWith('  ')) return classes.codeSame;
      if (line.startsWith('+ ')) return classes.codeAdded;
      if (line.startsWith('- ')) return classes.codeDeleted;
      isDiff = false;
    }
  );
  const toCopy = () => {
		copy(children);
		setCopied(true);
	}
  if (isDiff) {
    children = lines.map(l => l.substring(2)).join('\n');
  }

  return (
		
		<div style={{position:"relative"}}>
		
			<Highlight {...defaultProps} code={children} language={language} theme={HighlightTheme}>
				{({ style, tokens, getLineProps, getTokenProps }) => (
					<pre className={classes.codeBox} style={{ ...style }}>
						{tokens.map((line, i) => {
							const props = getLineProps({ line, key: i });
							if (isDiff) props.className = colors[i];
							return (
								<div key={i} {...props}>
									{line.map((token, key) => (
										<span key={key} {...getTokenProps({ token, key })} />
									))}
								</div>
							);
						})}
					</pre>
				)}
			</Highlight>
			<Tooltip title={isCopied?'Copied':'Copy'}>
				{isCopied
					?<CheckIcon className={classes.copyIcon} onClick={toCopy}/>
					:<ContentCopyIcon className={classes.copyIcon} onClick={toCopy}/>
				}
			</Tooltip>
		</div>
  );
}

export const DocContext = React.createContext();

const DocLink = ({ children, href }) => {
  const classes = useStyles();
  const { lang } = React.useContext(DocContext);
  if (href.startsWith('#')) {
    return (
      <Link to={href} className={classes.link} style={{color:'#00adef'}}>
        {children}
      </Link>
    );
  } else if (href.startsWith('/')) {
    return (
      <Link to={`/pipy/docs/${lang}${href}`} className={classes.link} style={{color:'#00adef'}}>
        {children}
      </Link>
    );
  } else {
    return (
      <a href={href} target="_blank" className={classes.link} style={{color:'#00adef'}}>
        {children}
        {<ExternalLinkIcon fontSize="small" className={classes.externalLinkIcon}/>}
      </a>
    );
  }
}

const Tip = ({ children }) => {
  const classes = useStyles();
  return (
    <blockquote className={classes.tip}>
      <div className={classes.tipIcon}><TipIcon fontSize="large"/></div>
      <div className={classes.tipText}>{children}</div>
    </blockquote>
  )
}

const components = {
  h1: makeStyledTag('h1'),
  h2: makeStyledTag('h2'),
  h3: makeStyledTag('h3'),
  h4: makeStyledTag('h4'),
  li: makeStyledTag('li', 'p'),
  p: makeStyledTag('p'),
  a: DocLink,
  table: makeStyledTag('table'),
  blockquote: Tip,
  code: makeStyledTag('span', 'inlineCode'),
  img: makeStyledTag('img'),
}

const Comment = ({ comment }) => {
  const classes = useStyles();
  return (
    <div className={classes.description}>
      <ReactMarkdown components={components}>
        {comment?.summary || '_No description_'}
      </ReactMarkdown>
    </div>
  );
}

const Summary = () => {
  const classes = useStyles();
  let { typedoc } = React.useContext(DocContext);
  let comment = typedoc.comment;
  if (typedoc.kindString === 'Method' || typedoc.kindString === 'Function') {
    comment = typedoc.signatures[0].comment;
  }
  return (
    <div className={classes.p}>
      <ReactMarkdown components={components}>
        {comment?.summary || '_No description_'}
      </ReactMarkdown>
    </div>
  );
}

const Classes = () => {
  const classes = useStyles();
  let { typedoc, lang, path } = React.useContext(DocContext);
  if (path.endsWith('/')) path = path.substring(0, path.length - 1);
  return (
    <React.Fragment>
      {typedoc.classes.map(
        child => [
          <Link
            className={classes.memberName}
            to={`/pipy/docs/${lang}/${path}/${child.name}`}
          >
            {child.name}
          </Link>,
          <Comment comment={child.comment}/>
        ]
      ).flat()}
    </React.Fragment>
  );
}

const Variables = () => {
  const classes = useStyles();
  let { typedoc, lang, path } = React.useContext(DocContext);
  if (path.endsWith('/')) path = path.substring(0, path.length - 1);
  return (
    <React.Fragment>
      {typedoc.variables.map(
        child => [
          <Link
            className={classes.memberName}
            to={`/pipy/docs/${lang}/${path}/${child.name}`}
          >
            {child.name}
          </Link>,
          <Comment comment={child.comment}/>
        ]
      ).flat()}
    </React.Fragment>
  );
}

const Functions = () => {
  const classes = useStyles();
  let { typedoc, lang, path } = React.useContext(DocContext);
  if (path.endsWith('/')) path = path.substring(0, path.length - 1);
  return (
    <React.Fragment>
      {typedoc.functions.map(
        child => child.overloads.map(
          sig => [
            <Prototype
              name={child.name}
              link={`/pipy/docs/${lang}/${path}/${child.name}`}
              parameters={sig.parameters}
            />,
            <Comment comment={sig.comment}/>
          ]
        )
      ).flat()}
    </React.Fragment>
  );
}

const Prototype = ({ name, parameters, link }) => {
  const classes = useStyles();
  return (
    <p>
      {link ? (
        <Link className={classes.memberName} to={link}>
          {name}
        </Link>
      ): (
        <span className={classes.parameterName}>
          {name}
        </span>
      )}
      <span className={classes.parameterName}>
        ({(parameters?.map?.(
          ({ name, flags }) => (flags?.isRest ? '...' : '') + name + (flags?.isOptional ? '?' : '')
        ) || []).join(', ')})
      </span>
    </p>
  );
}

const Constructor = () => {
  let { typedoc, lang, path } = React.useContext(DocContext);
  if (path.endsWith('/')) path = path.substring(0, path.length - 1);
  let className = typedoc.name;
  if (typedoc.memberOf) className = typedoc.memberOf + '.' + className;
  return (
    <React.Fragment>
      {typedoc.constructors.map(
        child => child.overloads.map(
          sig => [
            <Prototype
              name={`new ${className}`}
              link={`/pipy/docs/${lang}/${path}/new`}
              parameters={sig.parameters}
            />,
            <Comment comment={sig.comment}/>
          ]
        )
      ).flat()}
    </React.Fragment>
  );
}

const Properties = () => {
  const classes = useStyles();
  let { typedoc, lang, path } = React.useContext(DocContext);
  if (path.endsWith('/')) path = path.substring(0, path.length - 1);
  return (
    <React.Fragment>
      {typedoc.properties.map(
        child => [
          <Link
            className={classes.memberName}
            to={`/pipy/docs/${lang}/${path}/${child.name}`}
          >
            {child.name}
          </Link>,
          <Comment comment={child.comment}/>
        ]
      ).flat()}
    </React.Fragment>
  );
}

const Methods = () => {
  let { typedoc, lang, path } = React.useContext(DocContext);
  if (path.endsWith('/')) path = path.substring(0, path.length - 1);
  return (
    <React.Fragment>
      {typedoc.methods.map(
        child => child.overloads.map(
          sig => [
            <Prototype
              name={child.name}
              link={`/pipy/docs/${lang}/${path}/${child.name}`}
              parameters={sig.parameters}
            />,
            <Comment comment={sig.comment}/>
          ]
        )
      ).flat()}
    </React.Fragment>
  );
}

const StaticMethods = () => {
  let { typedoc, lang, path } = React.useContext(DocContext);
  if (path.endsWith('/')) path = path.substring(0, path.length - 1);
  return (
    <React.Fragment>
      {typedoc.staticMethods.map(
        child => child.overloads.map(
          sig => [
            <Prototype
              name={child.name}
              link={`/pipy/docs/${lang}/${path}/${child.name}`}
              parameters={sig.parameters}
            />,
            <Comment comment={sig.comment}/>
          ]
        )
      ).flat()}
    </React.Fragment>
  );
}

const Parameters = () => {
  const classes = useStyles();
  let { typedoc } = React.useContext(DocContext);
  return (
    <React.Fragment>
      {typedoc.overloads.map(
        (sig, i) => {
          const returns = sig.comment?.blockTags?.find?.(t => t.tag === '@returns');
          return [
            <Prototype
              name={typedoc.name === 'constructor' ? 'new ' + typedoc.memberOf : typedoc.name}
              parameters={sig.parameters}
            />,
            (i > 0 && <Comment comment={sig.comment}/>),
            <div className={classes.parameterList}>
              {sig.parameters?.map?.(
                ({ name, flags, comment }) => [
                  <span className={classes.inlineCode}>
                    {flags?.isRest && '...'}
                    {name}
                    {flags?.isOptional && '?'}
                  </span>,
                  <Comment comment={comment}/>
                ]
              )}
            </div>,
            returns && (
              <div className={classes.parameterList}>
                <span className={classes.returnValue}>Return Value</span>
                <div className={classes.description}>
                  <ReactMarkdown>{returns.content}</ReactMarkdown>
                </div>
              </div>
            )
          ];
        }
      ).flat()}
    </React.Fragment>
  );
}

const componentsApi = {
  ...components,

  code: SourceCode,
  inlineCode: makeStyledTag('span', 'inlineCode'),

  Summary,
  Classes,
  Variables,
  Functions,
  Constructor,
  Properties,
  Methods,
  StaticMethods,
  Parameters,
  FilterDiagram,
};

const LANGS = ['en', 'zh', 'jp'];

const LANGNAMES = {
  'en': 'English',
  'zh': '中文',
  'jp': '日本語',
};

const DocMdx = ({ data }) => {
	let { t ,i18n} = useTranslation()
  const classes = useStyles();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const [openSidebar, setOpenSidebar] = useState(false);
  const [navCurrent, setNavCurrent]  = useState({title:'',href:''});

  const handleSidebarOpen = () => {
    setOpenSidebar(true);
  };

  const handleSidebarClose = () => {
    setOpenSidebar(false);
  };
  let title = '';
  let current = 0;
  const loc = useLocation();
  const open = isMd ? false : openSidebar;
  const slugger = new Slugger;

  let lang = 'en';
  let path = loc.pathname;
  
  if (path.startsWith('/pipy/docs/')) {
    path = path.substring(11);
    const i = path.indexOf('/');
    if (i >= 0) {
      const s = path.substring(0, i);
      if (LANGS.includes(s)) {
        lang = s;
        path = path.substring(i + 1);
      }
    }
  }
	React.useEffect(() => {
	  i18n.changeLanguage(lang);
	}, []);

  const typedoc = JSON.parse(data?.typeDoc?.internal?.content || 'null');

  return (data?
    <DocContext.Provider value={{ typedoc, lang, path }}>
		<Container paddingY={4}>
      <AppBar
        position={'fixed'}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar pages={pages} nodes={data?data.allMdx.nodes:[]} onSidebarOpen={handleSidebarOpen} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={handleSidebarClose}
        open={open}
				pages={[]}
        current={current}
        nodes={data?data.allMdx.nodes:[]}
        variant={isMd ? 'permanent' : 'temporary'}
      />
      <main>
        <Box height={{ xs: 58, sm: 66, md: 71 }} />
        <Box
          display="flex"
          flex="1 1 auto"
          overflow="hidden"
          paddingLeft={{ md: '256px' }}
        >
          {data?<Box display="flex" flex="1 1 auto" overflow="hidden">
            <Box flex="1 1 auto" height="100%" overflow="auto" style={{position:'relative'}}>
              <Box sx={{padding:{ md: '40px 230px 40px 30px', xs: '40px 20px 40px 20px' }}}>
								<Typography component="h1" className={classes[`title_${lang}`] || classes.title}>
									{data.mdx.frontmatter.title}
								</Typography>
								<MDXProvider components={componentsApi}>
									<MDXRenderer headings={data.mdx.headings}>
									{data.mdx.body}
									</MDXRenderer>
								</MDXProvider>
								
								{data.mdx.headings.length > 1 ?
									<Box className={classes.toc} sx={{ display: { md: 'block', xs: 'none' } }} style={{ height: ((105+40*data.mdx.headings.length) + 'px') }}>
										<ul className={classes.tocList}>
											{data.mdx.headings.map(
												({ value, depth }) => (
													<a
														key={value}
														href={'#' + slugger.slug(value)}
														className={classes.tocListItem}
													>
														<li style={{ marginInlineStart: depth * 10 }}>
															{value}
														</li>
													</a>
												)
											)}
										</ul>
									</Box> : null
								}
								</Box>
								<Divider />
								<Container>
									<Footer pages={pages}/>
								</Container>
							</Box>
						</Box>
						:null}
					</Box>
				</main>
			</Container>
    </DocContext.Provider>
	:null
  );
};

// {members:{instance:[]}}
DocMdx.propTypes = {
  children: PropTypes.node
};

export default DocMdx;
